import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import PropTypes from "prop-types";
import parse from "html-react-parser";
import { visuallyHidden } from "@mui/utils";
import ReactDOMServer from "react-dom/server";
import { useTheme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DialogTitle from "@mui/material/DialogTitle";
import TableHead from "@mui/material/TableHead";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableContainer from "@mui/material/TableContainer";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import SearchIcon from "@mui/icons-material/Search";
import LastPageIcon from "@mui/icons-material/LastPage";
import { AnimatePresence } from "framer-motion";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import TablePagination from "@mui/material/TablePagination";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CircularProgress from "@mui/material/CircularProgress";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import FormControlLabel from "@mui/material/FormControlLabel";
import config_settings from "../../config/config";
import CustomDropdown from "../../components/CommentLibrary/CustomDropdown";
import SummaryRating from "../../components/CommentLibrary/SummaryRating";
import CommentTags from "../../components/CommentLibrary/CommentTags";
import { stableSort } from "./CommentLibraryHelper";
import { filterObjectsUptoCategory, timestamp } from "../../utils/services";
import FilterTags from "../../components/CommentLibrary/FilterTags";
import uuid from "react-uuid";
import AddNewCommentNN from "./AddNewCommentNN";
import toast from "react-hot-toast";
import { addMultipleGlobalRating, getCommentLibraryList, getCommentLibraryTagList, GetRatingsList, updateTEData } from "../../store/action";
import { collectMissingRatings, libraryRatingId, toRatingPayload, withSequentialOrder } from "./lineRatingUtils";

const STATUS_MAP = {
  all: undefined,
  used: 'used',
  unused: 'unused',
  summary: 'summary',
  deleted: 'deleted',
};

// A narrative comment carries `comment_text`/summary fields, the non-narrative table
// renders `comment` and never shows a summary, so reshape the row before it is used.
// Ratings are flattened to Id/Name/Color because that is the shape the rating column
// and the texas `rating_data` lookup in handleAddSelectedComment both expect.
const toNonNarrativeRow = (row) => ({
  ...row,
  comment: row?.comment_text ?? row?.comment ?? "",
  location: row?.location ?? "",
  comment_dropdowns: row?.comment_dropdowns ?? [],
  ratings: (row?.ratings || []).map((item) => ({
    ...item,
    Id: item?.rating?.id ?? item?.rating?.pivot?.rating_id ?? item?.pivot?.rating_id ?? item?.Id ?? item?.id,
    Name: item?.rating?.name ?? item?.Name ?? item?.name,
    Color: item?.rating?.color ?? item?.Color ?? item?.color,
    Definition: item?.rating?.definition ?? item?.Definition ?? item?.definition ?? "",
    include_in_summary: item?.rating?.include_in_summary ?? item?.include_in_summary ?? 0,
  })),
});

let BASE_URL;

const origin = () => {
  let origin = window.location.origin.toLowerCase();
  if (origin.includes("https://")) origin = origin.replace("https://", "");
  else origin = origin.replace("http://", "");
  BASE_URL = config_settings[origin]?.SERVER_URL;
};

origin();

function getComparator(order, orderBy) {
  const descendingComparator = (a, b, orderBy) => {
    if (!(orderBy in a) || !(orderBy in b)) {
      return 0;
    }
    if (typeof a[orderBy] === "string" && typeof b[orderBy] === "string") {
      const lowercaseA = (a[orderBy] || "")
        ?.replace(/<[^>]*>| /g, "")
        ?.replace(/\s/g, "")
        ?.toLowerCase();
      const lowercaseB = (b[orderBy] || "")
        ?.replace(/<[^>]*>| /g, "")
        ?.replace(/\s/g, "")
        ?.toLowerCase();
      if (lowercaseA < lowercaseB) {
        return 1;
      } else if (lowercaseA > lowercaseB) {
        return -1;
      }
    }
    if (a[orderBy] < b[orderBy]) {
      return 1;
    } else if (a[orderBy] > b[orderBy]) {
      return -1;
    }
    return 0;
  };
  return order === "desc" ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const NonNarrativeAddComment = ({
  open,
  setState,
  editComment,
  recommendation,
  disableCommentBtn,
  addNewCommentHandle,
  categoryId,
  categoryName,
  lineId,
  templateId,
  AllCategoryList,
  setAllCategoryList,
  updateTemplate,
  cmtLocHolder
}) => {
  const storeData = useSelector((state) => state.commentLibrary);
  const categories = useSelector((state) => state.templates)?.categoryList;
  const templateList = useSelector((state) => state?.templates?.templateList);
  // The company rating pool — the only place a definition can be read from for a rating the
  // template doesn't carry yet (the comment library rows don't include it).
  const ratingsPool = useSelector((state) => state?.templates?.ratingsList?.data);
  // Only global-rating templates share the company pool, so only they read from or refetch it.
  const isGlobalRatingEnabled = !!templateList?.is_global_rating_enabled;
  const categoryTags = AllCategoryList?.template?.categories?.find((category) => category.category_id === categoryId)?.nn_categories_tags;
  const [editorContent, setEditorContent] = useState({ innerHTML: "", dropdownList: [] });
  const [stateData, setStateData] = useState({
    expandedRows: [],
    commentListData: null,
    currentPage: 1,
    debouncedSearch: "",
    value: 0,
    commentTitle: "",
    addToLibrary: false,
    orderBy: "",
    order: "asc",
    page: 0,
    isLoading: false,
    selected: [],
    newlyAddedRows: [],
    filterType: 'all',
    selectedFilterTagID: [],
    totalRowCount: 0,
    totalPages: 0,
    tagsList: [],
    rowsPerPage: parseInt(sessionStorage.getItem("commentsRowPerPage"), 10) || 10,
    hasCommentsFromAPI: false,
    searchText: "",
    commentTag: [], commentRating: [], commentLocation: []
  });
  const { expandedRows, commentListData, currentPage, debouncedSearch, value, searchText, commentTitle, addToLibrary, orderBy, order, page, isLoading, selected, newlyAddedRows, filterType, selectedFilterTagID, totalRowCount, totalPages, tagsList, rowsPerPage, hasCommentsFromAPI, commentTag, commentRating, commentLocation } = stateData
  const shouldShowCommentLibraryTab = hasCommentsFromAPI;
  const newCommentTabIndex = shouldShowCommentLibraryTab ? 1 : 0;
  const isNewCommentTab = value === newCommentTabIndex;
  const dispatch = useDispatch();
  const isFirstFetch = useRef(false);
  const isHtCompany = storeData?.isHTCompany;
  const isTexasNarrativeCL = Boolean(isHtCompany && AllCategoryList?.template?.is_texas);
  const showRatingColumn = !isTexasNarrativeCL;

  useEffect(() => {
    if (categoryTags?.length > 0 && open) {
      let newData = categoryTags.map(tag => tagsList?.some(t => t.id === tag?.tag?.id) ? tag?.tag?.id : null)
      setStateData(prevState => ({ ...prevState, selectedFilterTagID: newData?.filter(id => id !== null) }));
    }
  }, [categoryTags, open, tagsList])

  const handleClose = () => {
    setState((prevState) => ({ ...prevState, isCommentDialogOpen: false }));
    setEditorContent(prevState => ({ ...prevState, innerHTML: "" }));
    setStateData(prevState => ({ ...prevState, commentTitle: "", addToLibrary: false, value: 0, page: 0, orderBy: "", order: "asc", selected: [], isLoading: false, selectedFilterTagID: [], totalRowCount: 0, totalPages: 0, rowsPerPage: 10, expandedRows: [], searchText: "", debouncedSearch: "", commentTag: [], commentRating: [], commentLocation: [] }));
  };

  // A rating picked for a new comment can come from the company pool without being on the template
  // yet. The comment is saved first (it edits the categories in place), then the rating is added to
  // `rating_data` and registered, the same way a library comment's new rating is.
  const addPickedRatingsToTemplate = (pickedRatings) => {
    const templateRatings = Array.isArray(AllCategoryList?.template?.rating_data)
      ? AllCategoryList.template.rating_data
      : [];
    const missingRatings = collectMissingRatings((pickedRatings || []).map(toTemplateRating), templateRatings);
    if (!AllCategoryList || missingRatings.length === 0) return;
    const updatedTemplateData = {
      ...AllCategoryList,
      template: {
        ...AllCategoryList.template,
        rating_data: withSequentialOrder([...templateRatings, ...missingRatings]),
      },
    };
    setAllCategoryList(updatedTemplateData);
    dispatch(updateTEData(updatedTemplateData));
    registerMissingRatings(missingRatings);
  };

  const createNewComment = async () => {
    await addNewCommentHandle(lineId, commentTitle, editorContent, addToLibrary, commentTag, commentRating, commentLocation);
    addPickedRatingsToTemplate(commentRating);
    setState((prevState) => ({ ...prevState, isCommentDialogOpen: false }));
    setEditorContent(prevState => ({ ...prevState, innerHTML: "" }));
    setStateData(prevState => ({ ...prevState, commentTitle: "", addToLibrary: false, commentTag: [], commentRating: [], commentLocation: [], value: 0 }));
  };

  const reIndexComments = (comments) => {
    comments.forEach((x, i) => {
      x.order = i + 1;
      x.updated_at = timestamp();
      x.is_selected_updated_at = timestamp(comments.length - x.order);
      return x;
    });
    return [...comments];
  };

  useEffect(() => {
    setEditorContent(prevState => ({ ...prevState, innerHTML: recommendation || "" }));
  }, []);

  // Refetched on every open so a library comment bringing in a rating the template doesn't have can
  // still be given that rating's definition. The pool is created and edited from the Comment Library
  // Ratings tab (and other tabs) without touching the store, so a copy cached earlier goes stale and
  // a new rating would be imported with no definition.
  // Adding is held until the refetch settles: a click that landed before it would build the rating
  // from the stale pool and save it with an empty definition. A failed refetch leaves the cached
  // pool in use.
  const [isPoolRefreshing, setIsPoolRefreshing] = useState(false);
  useEffect(() => {
    if (!open || !isGlobalRatingEnabled) {
      setIsPoolRefreshing(false);
      return undefined;
    }
    let isCurrent = true;
    setIsPoolRefreshing(true);
    dispatch(GetRatingsList())
      .catch(() => null)
      .finally(() => {
        if (isCurrent) setIsPoolRefreshing(false);
      });
    return () => {
      isCurrent = false;
    };
  }, [open, isGlobalRatingEnabled, dispatch]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setStateData(prevState => ({
        ...prevState,
        debouncedSearch: searchText?.trim() || "",
        page: prevState.page === 0 ? prevState.page : 0,
      }))
    }, 1000);
    return () => clearTimeout(handle);
  }, [searchText]);

  useEffect(() => {
    if (!open) return;
    if (value === 1) return;
    if (!sessionStorage.getItem("company-key")) return;
    if (isFirstFetch.current && categoryTags?.length > 0 && selectedFilterTagID.length === 0) {
      return;
    }
    isFirstFetch.current = false;
    async function fetchNarrativeComments() {
      try {
        setStateData(prevState => ({ ...prevState, isLoading: true }));
        const response = await dispatch(
          getCommentLibraryList(
            page + 1,
            null,
            { page: page + 1, filterByTagId: selectedFilterTagID, filterByType: filterType },
            debouncedSearch,
            rowsPerPage,
            true
          )
        );
        const data = response?.payload?.data ?? [];
        const totalCount = response?.payload?.total ?? data.length;
        const filtersApplied =
          !!debouncedSearch ||
          (Array.isArray(selectedFilterTagID) && selectedFilterTagID.length > 0) ||
          (filterType && filterType !== "all");
        setStateData(prevState => ({
          ...prevState,
          commentListData: Array.isArray(data) ? data.map(toNonNarrativeRow) : [],
          totalRowCount: totalCount,
          totalPages: Math.max(1, Math.ceil(totalCount / (rowsPerPage || 10))),
          isLoading: false,
          // The narrative endpoint has no `records_available`, so an empty filtered
          // result must not collapse the Comment Library tab.
          hasCommentsFromAPI: totalCount > 0 || filtersApplied,
        }));
      } catch (err) {
        setStateData(prevState => ({ ...prevState, isLoading: false }));
      }
    }
    async function fetchComments() {
      try {
        setStateData(prevState => ({ ...prevState, isLoading: true }));
        const companyID = sessionStorage.getItem("company-key");
        const params = {
          "company-key": companyID,
          page: page + 1,
          rows_per_page: rowsPerPage,
          sort_direction: order,
          sort_field: orderBy
        };
        const status = STATUS_MAP[filterType];
        if (status !== undefined) params.status = status;
        if (debouncedSearch) params.search = debouncedSearch;
        if (Array.isArray(selectedFilterTagID) && selectedFilterTagID?.length) {
          selectedFilterTagID?.forEach((id, idx) => {
            params[`filter_tags[${idx}]`] = id;
          });
        }
        const res = await axios.get(`${BASE_URL}/comments`, {
          params,
          headers: {
            "Content-Type": "application/json",
            "company-key": companyID,
          },
        });
        const data = res?.data?.data?.data ?? [];
        const pg = res?.data?.data?.pagination;
        const totalCount = pg?.total_count ?? data.length;
        setStateData(prevState => ({
          ...prevState,
          commentListData: Array.isArray(data) ? data : [],
          totalRowCount: totalCount,
          rowsPerPage: parseInt(pg?.rows_per_page ?? prevState.rowsPerPage),
          totalPages: pg?.total_pages ?? 1,
          isLoading: false,
          hasCommentsFromAPI: res.data?.data?.records_available,
        }));
      } catch (err) {
        setStateData(prevState => ({ ...prevState, isLoading: false }));
      }
    }
    if (isTexasNarrativeCL) fetchNarrativeComments();
    else fetchComments();
  }, [page, parseInt(rowsPerPage), filterType, selectedFilterTagID, debouncedSearch, value, orderBy, order, open, categoryTags, isTexasNarrativeCL]);

  // // ---------------- FETCH LOCATIONS (for Location Autocomplete) ----------------
  // useEffect(() => {
  //   if (!sessionStorage.getItem("company-key")) return;
  //   const controller = new AbortController();
  //   async function fetchLocations() {
  //     try {
  //       const companyID = sessionStorage.getItem("company-key");
  //       const res = await axios.get(`${BASE_URL}/locations`, {
  //         // params: { company_id: companyID },
  //         signal: controller.signal,
  //         headers: { "Content-Type": "application/json", "company-key": companyID },
  //       });
  //       const data = res?.data?.data ?? [];
  //       setLocationsList(Array.isArray(data) ? data : []);
  //     } catch (err) {
  //       if (!axios.isCancel(err)) console.error("LOCATIONS API error:", err);
  //     }
  //   }
  //   fetchLocations();
  //   return () => controller.abort();
  // }, [sessionStorage.getItem("company-key")]);

  // ---------------- FETCH RATINGS (for Rating Autocomplete) ----------------
  // useEffect(() => {
  //   if (!sessionStorage.getItem("company-key")) return;
  //   const controller = new AbortController();
  //   async function fetchRatings() {
  //     try {
  //       const companyID = sessionStorage.getItem("company-key");
  //       const res = await axios.get(`${BASE_URL}/ratings`, {
  //         signal: controller.signal,
  //         headers: { "company-key": companyID },
  //       });
  //       const data = res?.data?.data ?? [];
  //       setRatingsList(Array.isArray(data) ? data : []);
  //     } catch (err) {
  //       if (!axios.isCancel(err)) console.error("RATINGS API error:", err);
  //     }
  //   }
  //   fetchRatings();
  //   return () => controller.abort();
  // }, [sessionStorage.getItem("company-key")]);
  // ---------------- FETCH TAGS ----------------
  useEffect(() => {
    if (value === 1) return;
    if (!sessionStorage.getItem("company-key")) return;
    const controller = new AbortController();
    async function fetchTags() {
      try {
        const companyID = sessionStorage.getItem("company-key");
        const res = await axios.get(`${BASE_URL}/filter-tag-list`, {
          params: { company_id: companyID },
          signal: controller.signal,
          headers: { "company-key": companyID, },
        });
        setStateData(prevState => ({ ...prevState, tagsList: res?.data?.data ?? [] }));
      } catch (err) {
        if (!axios.isCancel(err)) console.error("TAGS API error:", err);
      }
    }
    // The rows now come from the narrative library, so the filter has to offer its tags.
    async function fetchNarrativeTags() {
      try {
        const response = await dispatch(getCommentLibraryTagList());
        setStateData(prevState => ({ ...prevState, tagsList: response?.payload ?? [] }));
      } catch (err) {
        console.error("TAGS API error:", err);
      }
    }
    if (isTexasNarrativeCL) fetchNarrativeTags();
    else fetchTags();
    return () => controller.abort();
  }, [sessionStorage.getItem("company-key"), value, isTexasNarrativeCL]);

  const EnhancedTableHead = (props) => {
    const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
    const createSortHandler = (property) => (event) => {
      onRequestSort(event, property);
    };
    return (
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox"></TableCell>
          {headCells.map((headCell) => (
            <TableCell key={headCell.id} align={"left"} padding={headCell.disablePadding ? "none" : "normal"} sortDirection={orderBy === headCell.id ? order : false} className="cl-table-head">
              <TableSortLabel
                active={orderBy === headCell.id}
                style={{ cursor: headCell.id === "rating" ? "default" : "pointer" }}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={headCell.id === "rating" ? null : createSortHandler(headCell.id)}
                IconComponent={() => (headCell.id !== "expand_icon" && headCell.id !== "global" && headCell.id !== "rating" ? <UnfoldMoreIcon className="cl-sort-icon" /> : "")}
              >
                {filterType == "deleted" && headCell.label == "Last modified" ? "Date deleted" : headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(["asc", "desc"]).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
  };
  const headCells = [
    {
      id: "expand_icon", numeric: false, disablePadding: true,
      label: <LastPageIcon className="cl-table-exp-icon" onClick={toggleAllRowsExpansion} />,
    },
    { id: "title", numeric: false, disablePadding: false, label: "Title", },
    { id: "comment", numeric: false, disablePadding: false, label: "Comment", },
    ...(showRatingColumn ? [{ id: "rating", numeric: false, disablePadding: false, label: "Rating" }] : []),
    { id: "updated_at", numeric: false, disablePadding: false, label: "Last modified" },
    { id: "global", numeric: false, disablePadding: true, label: "" },
  ];

  // A library comment carries its ratings from the company pool, so a field can live under the
  // pivot, on the nested rating, or already flattened onto the row by `toNonNarrativeRow` — hence
  // both casings are read. The definition is the one the library row usually leaves out, which is
  // why it was reaching the API empty; it is also looked up on the template's own copy of the
  // rating, since both lists come from the same company pool.
  const toTemplateRating = (obj) => {
    const rating = obj?.rating ?? {};
    const Id = libraryRatingId(obj);
    const sameId = (candidate) => String(candidate?.Id ?? candidate?.id) === String(Id);
    const onTemplate = (AllCategoryList?.template?.rating_data || []).find(sameId);
    // A rating the template doesn't have yet has no copy to read from, so the company pool is the
    // last resort — without it the rating lands on the template with no definition and the Edit
    // rating dialog opens empty. Non-global templates don't use the pool, so it is not read for them.
    const inPool = isGlobalRatingEnabled ? (ratingsPool || []).find(sameId) : undefined;
    return {
      Id,
      Name: rating.name || obj?.Name || obj?.name,
      Color: rating.color || obj?.Color || obj?.color,
      Definition:
        rating.definition ||
        obj?.Definition ||
        obj?.definition ||
        onTemplate?.Definition ||
        onTemplate?.definition ||
        inPool?.definition ||
        inPool?.Definition ||
        "",
      is_default: 0,
      include_in_summary: rating.include_in_summary || obj?.include_in_summary,
      updated_at: timestamp(),
    };
  };

  // A comment added from the library can carry a rating the template doesn't have yet. Adding it
  // to `rating_data` locally is not enough — the rating has to be registered on the template with
  // POST template/ratings, otherwise it is gone on the next load. The endpoint replaces the whole
  // list, so the ratings already on the template are re-sent alongside the new ones.
  // Only global-rating templates share the company rating pool, so the call is skipped otherwise.
  const registerMissingRatings = (missingRatings) => {
    const id = parseInt(templateId ?? AllCategoryList?.template_id);
    if (!isGlobalRatingEnabled) return;
    if (!missingRatings?.length || !id) return;
    const added = missingRatings.map(toRatingPayload).filter((rating) => !Number.isNaN(rating.Id));
    if (!added.length) return;
    const existing = (AllCategoryList?.template?.rating_data || []).map(toRatingPayload);
    dispatch(
      addMultipleGlobalRating({
        template_id: id,
        rating_data: withSequentialOrder([...existing, ...added]),
      })
    ).catch((error) => toast.error(error?.message || "Failed to add ratings"));
  };

  const handleAddSelectedComment = () => {
    let newCommentList = [];
    const isTexasTemplate = !!AllCategoryList?.template?.is_texas;
    const templateRatings = Array.isArray(AllCategoryList?.template?.rating_data)
      ? AllCategoryList.template.rating_data
      : [];
    const missingRatings = [];
    const catList = AllCategoryList?.template?.categories;
    const categoriesData = catList.findIndex((cat) => cat?.id === categoryId);
    let lineIndex = catList[categoriesData]?.lines?.findIndex((line) => line?.uuid === lineId);
    selected?.forEach((item, index) => {
      const ratingUuid = uuid();
      const cmtRating = [
        {
          comment_input_control_id: ratingUuid,
          uuid: ratingUuid,
          updated_at: timestamp(),
          prompt: "Rating",
          values: [],
          type: "Rating",
          data: [],
        },
      ];
      const newUuid = uuid();
      const modifiedTag = item?.tags?.map((obj) => ({
        id: obj?.tag?.id || obj?.pivot?.tag_id,
        name: obj?.tag?.name || obj?.name,
        color: obj?.tag?.color || obj?.color,
      }));
      let modifiedRating = []
       if (!isTexasTemplate) {
        modifiedRating = item?.ratings?.map(toTemplateRating);
        // Ratings the template doesn't have yet — collected across every selected comment, so the
        // same new rating picked up twice is only added once.
        collectMissingRatings(modifiedRating, [...templateRatings, ...missingRatings]).forEach(
          (rating) => missingRatings.push(rating)
        );
      } else {
        // Texas/TREC templates have a fixed rating list, so a library rating that isn't already on
        // the template is dropped rather than added.
        modifiedRating = templateRatings
          .filter((obj) => item?.ratings?.some((r) =>
            r.Name === obj.Name &&
            r.Color === obj.Color &&
            parseInt(r.include_in_summary) === parseInt(obj.include_in_summary)
          ))
          ?.map(toTemplateRating);
      }
      const modifiedCommentDropdown = item?.comment_dropdowns.map((obj) => ({
        dropdown_uuid: obj?.dropdown_uuid,
        dropdowns_values: obj?.dropdowns_values,
        selected_value: obj?.selected_value,
        list_guid: obj?.list_guid,
      }));
      const dropdownPattern = /\[dropdown ([a-f0-9-]+)\]/g;
      const updatedCommentText = item?.comment?.replace(dropdownPattern, (match, id) => {
        return `<span contenteditable="false">${match}</span>`;
      });
      cmtRating[0].data = modifiedRating;
      cmtLocHolder[0].data = [item?.location];
      cmtLocHolder[0].prompt = "Location";
      cmtLocHolder[0].is_manual_promt_edit = 0;
      cmtLocHolder[0].updated_at = timestamp();
      let newInputControls = Array.isArray(modifiedRating) && [cmtRating[0]];
      if (newInputControls?.length) newInputControls = item?.location?.length > 0 ? [...newInputControls, cmtLocHolder[0]] : newInputControls;
      const newCommentIndex = catList[categoriesData]?.lines[lineIndex]?.comments?.findIndex((comment) => comment?.uuid === item?.uuid);
      newCommentList.push({
        comment_id: newUuid || "",
        uuid: newCommentIndex === -1 ? uuid() : item?.uuid || "",
        updated_at: timestamp(),
        prompt: item?.title || "",
        recommendation: updatedCommentText || "",
        comment_dropdowns: modifiedCommentDropdown || [],
        tags: modifiedTag || [],
        default_location: item?.location || "",
        is_deleted: 0,
        is_selected: 0,
        comment_input_controls: newInputControls || [],
      });
    });
    const commentsAddedToList = reIndexComments([...catList[categoriesData]?.lines[lineIndex]?.comments, ...newCommentList]);
    catList[categoriesData].lines[lineIndex].comments = [...commentsAddedToList];
    catList[categoriesData].lines[lineIndex].updated_at = timestamp();
    filterObjectsUptoCategory(catList, categoryId, dispatch, categories);
    if (AllCategoryList) {
      // Re-checked against the current list rather than appended blindly, so a rating that is
      // already there is not added a second time. Renumbered so the added ratings keep the position
      // they were appended at instead of jumping to the top on reload, and so local order matches
      // the list that is posted.
      const stillMissing = collectMissingRatings(missingRatings, templateRatings);
      const updatedTemplateData = {
        ...AllCategoryList,
        template: {
          ...AllCategoryList.template,
          categories: (catList || []).map((cat) => ({ ...cat, updated_at: timestamp() })),
          rating_data: stillMissing.length > 0
            ? withSequentialOrder([...templateRatings, ...stillMissing])
            : templateRatings,
        },
      };
      setAllCategoryList(updatedTemplateData);
      // Autosave builds its update-template payload from `templates.templateData`, which is only
      // filled when the template loads — so a rating added here has to land there too, otherwise
      // the save posts the rating list from load time and the new rating is gone on the next load.
      // The same object goes to both, the way the load does it, so the two stay in sync.
      dispatch(updateTEData(updatedTemplateData));
    }
    updateTemplate("noToast");
    registerMissingRatings(missingRatings);
    handleClose();
  };

  const handleChangeRowsPerPage = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setStateData(p => ({ ...p, rowsPerPage: newSize, page: 0, expandedRows: [], }));
  };

  const toggleRowExpansion = (rowId) => {
    setStateData((prevState) => ({ ...prevState, expandedRows: expandedRows.includes(rowId) ? expandedRows.filter((id) => id !== rowId) : [...expandedRows, rowId] }));
  };

  const isRowExpanded = (rowId) => expandedRows.includes(rowId);
  function toggleAllRowsExpansion() {
    const allRowIds = commentListData.map((row) => row.id);
    setStateData((prevState) => ({ ...prevState, expandedRows: expandedRows.length === allRowIds.length ? [] : allRowIds }));
  }

  const handleRequestSort = (event, property) => {
    if (property === "expand_icon") return;
    const isAsc = orderBy === property && order === "asc";
    setStateData((prevState) => ({ ...prevState, order: isAsc ? "desc" : "asc", orderBy: property }));
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const selectedComment = commentListData.map((n) => n.id);
      setStateData((prevState) => ({ ...prevState, selected: selectedComment }));
      return;
    }
    setStateData((prevState) => ({ ...prevState, selected: [] }));
  };
  const handleChangePage = (event, newPage) => {
    setStateData((prevState) => ({ ...prevState, currentPage: newPage, page: newPage, expandedRows: [] }));
  };

  const hasActiveFilters =
    !!debouncedSearch ||
    (Array.isArray(selectedFilterTagID) && selectedFilterTagID.length > 0) ||
    (filterType && filterType !== "all");

  function TablePaginationActions(props) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (event) => {
      onPageChange(event, 0);
    };

    const handleBackButtonClick = (event) => {
      onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event) => {
      onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event) => {
      onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
      <Box sx={{ flexShrink: 0, ml: 2.5 }}>
        <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
          {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
        </IconButton>
        <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
          {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
        </IconButton>
        <IconButton onClick={handleNextButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
          {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
        </IconButton>
        <IconButton onClick={handleLastPageButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="last page">
          {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
        </IconButton>
      </Box>
    );
  }

  TablePaginationActions.propTypes = {
    count: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
  };

  function TablePaginationActionsHT(props) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;
    const handleFirstPageButtonClick = (event) => {
      onPageChange(event, 0);
    };
    const handleBackButtonClick = (event) => {
      onPageChange(event, page - 1);
    };
    const handleNextButtonClick = (event) => {
      onPageChange(event, page + 1);
    };
    const handleLastPageButtonClick = (event) => {
      const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
      onPageChange(event, lastPage);
    };

    return (
      <Box sx={{ flexShrink: 0, ml: 5 }}>
        <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page" sx={{ ml: 3 }}>
          {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
        </IconButton>
        <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page" sx={{ ml: 3 }}>
          {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
        </IconButton>
        <IconButton onClick={handleNextButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page" sx={{ ml: 3 }}>
          {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
        </IconButton>
        <IconButton onClick={handleLastPageButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="last page">
          {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
        </IconButton>
      </Box>
    );
  }

  TablePaginationActionsHT.propTypes = {
    count: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
  };

  function replaceDropdownsWithSelects(htmlString, dropdownValues, type, comment_id, comment_uuid) {
    const isValidSelectedValue = (val) => {
      if (val === null || val === undefined) return false;
      const str = String(val).trim();
      return str !== "" && str.toLowerCase() !== "null" && str.toLowerCase() !== "undefined" && !str.includes("------");
    };
    var uuidPattern = /\[dropdown ([a-f0-9-]+)\]/g;
    var uuids = htmlString?.match(uuidPattern);
    let singleUuidPattern = /\[dropdown ([a-f0-9-]+)\]/;
    const regex = /Â/g;
    function decodeEntities(encodedString) {
      const textarea = document.createElement("textarea");
      textarea.innerHTML = encodedString;
      return textarea.value;
    }
    if (uuids) {
      uuids?.forEach(function (uuid) {
        let uuidSingle = uuid.match(singleUuidPattern)[1];
        let commentDropValues = dropdownValues?.find((el) => el.dropdown_uuid === uuidSingle);
        let newDropdownValues = commentDropValues?.dropdowns_values || [];
        newDropdownValues = Array.isArray(newDropdownValues) && newDropdownValues?.filter((obj) => !obj?.toUpperCase()?.includes("XXXXX"));
        const hasValidSelectedValue = isValidSelectedValue(commentDropValues?.selected_value);
        if (hasValidSelectedValue && Array.isArray(newDropdownValues)) {
          const hasSelectedValue = newDropdownValues.some(val => val?.trim() === commentDropValues.selected_value?.trim());
          if (!hasSelectedValue) {
            newDropdownValues.push(commentDropValues.selected_value);
          }
        }
        if (Array.isArray(newDropdownValues) && !newDropdownValues?.map((value) => value?.toUpperCase())?.some((value) => value?.includes("------"))) {
          newDropdownValues?.unshift("-------------");
        }
        var selectHtml = React.createElement(CustomDropdown, {
          dropdownUuid: uuidSingle || "",
          commentUuid: comment_uuid || "",
          commentId: comment_id || "",
          type: type || "",
          options: newDropdownValues || [],
          selectedValue: hasValidSelectedValue ? commentDropValues.selected_value : "-------------",
        });
        htmlString = htmlString?.replace(uuid, ReactDOMServer.renderToStaticMarkup(selectHtml));
      });
    } else {
      var parser = new DOMParser();
      var doc = parser.parseFromString(htmlString, "text/html");
      var selectTags = doc.querySelectorAll("select");
      selectTags.forEach((select) => {
        let uuidSingle = select.getAttribute("id");
        let commentDropValues = dropdownValues?.find((el) => el.dropdown_uuid === uuidSingle);
        let newDropdownValues = commentDropValues?.dropdowns_values || [];
        newDropdownValues = Array.isArray(newDropdownValues) && newDropdownValues?.filter((obj) => !obj?.toUpperCase()?.includes("XXXXX"));
        const hasValidSelectedValue = isValidSelectedValue(commentDropValues?.selected_value);
        if (hasValidSelectedValue && Array.isArray(newDropdownValues)) {
          const hasSelectedValue = newDropdownValues.some(val => val?.trim() === commentDropValues.selected_value?.trim());
          if (!hasSelectedValue) {
            newDropdownValues.push(commentDropValues.selected_value);
          }
        }
        if (Array.isArray(newDropdownValues) && !newDropdownValues?.map((value) => value?.toUpperCase())?.some((value) => value?.includes("------"))) {
          newDropdownValues?.unshift("-------------");
        }
        var selectHtml = React.createElement(CustomDropdown, {
          dropdownUuid: uuidSingle || "",
          commentUuid: comment_uuid || "",
          commentId: comment_id || "",
          type: type || "",
          options: newDropdownValues || [],
          selectedValue: hasValidSelectedValue ? commentDropValues.selected_value : "-------------",
        });
        let selectOuterHtml = select.outerHTML;
        htmlString = htmlString.replace(selectOuterHtml, ReactDOMServer.renderToStaticMarkup(selectHtml));
      });
    }
    const replaceInputs = (input_text) => {
      let container = document.createElement("div");
      container.innerHTML = input_text;
      container.innerHTML = container.innerHTML.replace(/\[textbox(.*?)\]/g, (match, attributes) => {
        let idMatch = attributes.match(/id:([^;]+);?/);
        let valueMatch = attributes.match(/value:([^;]+);?/);
        let inputId = idMatch && idMatch[1].length > 30 ? idMatch[1].trim() : uuid();
        let value = valueMatch ? valueMatch[1].trim() : "";
        const safeValue = value.replace(/\\/g, "\\\\").replace(/"/g, "&quot;");
        return `<input id="${inputId}" disabled="true" value="${safeValue}" comment_id="${comment_id}" class="${type}-cl-input" title="${safeValue}" style="width: 104px;
                background-color: #f1f9fe;
                height: 24px;
                overflow-wrap: break-word;
                padding: 1px 17px 3px 6px;
                border-radius: 4px;
                border: 1px solid #0F75BC;
                text-overflow: ellipsis;
                white-space: nowrap;
                appearance: none;
                -webkit-appearance: none;
                -moz-appearance: none;
                font-size: 14px;
                color: #3B3B3B;
                font-style: normal;
                font-weight: 600;
                max-width: 200px;
                min-width: 104px;"
                onblur="handleChangeInput(inputId, value)"
                />`;
      });
      return container.innerHTML;
    }
    return parse(decodeEntities(replaceInputs(htmlString?.replace(regex, "")?.replace(/Ã/g, ""))));
  }

  const handleClickChangeDropdown = (event, id, row) => {
    event.stopPropagation();
    if (!isRowExpanded(id)) toggleRowExpansion(row.id);
    const selectedIndex = selected.findIndex(item => item.uuid === row.uuid);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = [...selected, row];
    } else if (selectedIndex === 0) {
      newSelected = selected.slice(1);
    } else if (selectedIndex === selected.length - 1) {
      newSelected = selected.slice(0, -1);
    } else if (selectedIndex > 0) {
      newSelected = [...selected.slice(0, selectedIndex), ...selected.slice(selectedIndex + 1)];
    }
    setStateData((prevState) => ({ ...prevState, selected: newSelected, }));
  };

  const isSelected = (id) => selected.some(item => item.uuid === id);
  const visibleRows = stableSort(commentListData, getComparator(order, orderBy), newlyAddedRows, currentPage);
  const preprocessHTMLContent = (htmlContent) => {
    const textContent = htmlContent?.replace(/<(?!input\b)[^>]+>/gi, "");
    const normalizedText = textContent?.replace(/&nbsp;/g, " ");
    return normalizedText?.trim();
  }

  let commentDropElements = document.querySelectorAll(".comment-cl-dropdown");
  commentDropElements?.forEach((element) => {
    const commentId = element.getAttribute("comment_id");
    const dropType = element.getAttribute("dropType");
    const commentUUId = element.getAttribute("comment_uuid");
    const commentDisabled = element.getAttribute("disabled");
    if (element !== null && element !== undefined) {
      element.onclick = (event) => {
        event.stopPropagation();
      }
    }
  });

  return (
    <Dialog className="comment-dialog-non-narrative" open={open} onClose={handleClose} aria-labelledby="category-modal" aria-describedby="category-modal-description">
      <DialogTitle pb={0}>
        <Grid container display={"flex"}>
          <Grid item xs={11} display={"flex"} justifyContent={"flex-start"}>
            <Typography className="non-narrative-comment-title">{`${categoryName ? categoryName + " -" : ""} ${editComment ? "Edit comment" : "Add comment"}`}</Typography>
          </Grid>
          <Grid item xs={1} display={"flex"} justifyContent={"flex-end"} alignItems={"center"} position={"relative"}>
            <div className="close-wrapper-new-category-right">
              <CloseIcon onClick={handleClose} />
            </div>
          </Grid>
        </Grid>
        <Tabs
          value={value}
          className="non-narrative-comment-tabs"
          onChange={(e, newValue) => setStateData(prevState => ({ ...prevState, value: newValue }))}
          aria-label="basic tabs example">
          {shouldShowCommentLibraryTab ? <Tab label={"Comment Library"} id={`simple-tab-0`} className="non-narrative-comment-tab" aria-controls={`simple-tabpanel-0`} /> : null}
          <Tab label={"New comment"} id={`simple-tab-1`} className="non-narrative-comment-tab" aria-controls={`simple-tabpanel-1`} />
        </Tabs >
      </DialogTitle >
      <DialogContent>
        <Grid container height={"100%"}>
          <Grid item xs={12}>
            <Divider />
            {shouldShowCommentLibraryTab ? (
              <TabPanel value={value} index={0} className="tab_panel_cLibrary">
                <Box>
                  <Grid container className="cl-search-container">
                    <Grid item xs={6} md={6} lg={2} className="cl-search-bar">
                      <div className="cl-search">
                        <TextField
                          id="search-bar"
                          variant="outlined"
                          placeholder="Search in table"
                          size="small"
                          value={searchText}
                          onChange={(e) => setStateData((prevState) => ({ ...prevState, searchText: e.target.value }))}
                        />
                        <SearchIcon className="cl-search-icon" />
                      </div>
                    </Grid>
                    <Grid item xs={6} md={6} lg={5}>
                      <FilterTags filterTagList={tagsList} filterType={filterType} setState={setStateData} state={stateData} />
                    </Grid>
                  </Grid>
                  {isLoading ? (
                    <Box className="loader-container-comment-library" sx={{ width: "100%", height: "100%" }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer className="cl-table-container-te" sx={{ overflowY: visibleRows?.length > 0 ? "auto" : "hidden !important", overflowX: "hidden" }}>
                      <Table stickyHeader sx={{ minWidth: 750, height: "100%" }} aria-labelledby="tableTitle" size={"medium"}>
                        <EnhancedTableHead
                          numSelected={selected.length}
                          order={order}
                          orderBy={orderBy}
                          onSelectAllClick={handleSelectAllClick}
                          onRequestSort={handleRequestSort}
                          rowCount={commentListData?.length}
                        />
                        <AnimatePresence>
                          <TableBody>
                            {visibleRows?.length > 0 ? visibleRows?.map((row, index) => {
                              const isNewlyAdded = newlyAddedRows[row.id];
                              const isItemSelected = isSelected(row.uuid);
                              const labelId = `enhanced-table-checkbox-${index}`;
                              return (
                                <TableRow
                                  key={row?.id}
                                  transition={{ duration: 0.4, ease: "easeOut" }}
                                  hover
                                  className={isNewlyAdded ? "new-row" : ""}
                                  aria-checked={isItemSelected}
                                  tabIndex={1}
                                  selected={isItemSelected}
                                  sx={{ cursor: "pointer", borderLeft: "1px solid blue" }}
                                >
                                  <TableCell
                                    padding="checkbox"
                                    onClick={(event) => handleClickChangeDropdown(event, row.id, row)}
                                    className={`cl-checkbox-col ${isRowExpanded(row.id) ? (isItemSelected ? "cl-check-active" : "") : ""}`}>
                                    <Checkbox
                                      sx={{
                                        height: "37.5px !important",
                                        width: "37.5px !important",
                                        "& .MuiSvgIcon-root": { fontSize: "20px !important" },
                                      }}
                                      color="primary"
                                      checked={isItemSelected}
                                    />
                                  </TableCell>
                                  <TableCell
                                    component="th"
                                    id={labelId}
                                    scope="row"
                                    padding="none"
                                    className="cl-row-status cl-exp-col"
                                  >
                                    <IconButton onClick={() => toggleRowExpansion(row.id)} style={{ marginLeft: "15px" }}>
                                      {isRowExpanded(row.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell className="cl-title-col" align="left">
                                    <div style={{ width: "150px" }} className={`cl-comment-title ${isRowExpanded(row.id) ? "expanded" : ""}`}>{row?.title}</div>
                                  </TableCell>
                                  <TableCell className="cl-comment-col" align="left">
                                    <div className={`comment-text ${isRowExpanded(row.id) ? "expanded" : ""}`} onClick={() => (!isRowExpanded(row.id) ? toggleRowExpansion(row.id) : undefined)}>
                                      {row?.comment !== null && replaceDropdownsWithSelects(row?.comment?.replace(/<\/?[^>]+(>|$)/g, ""), row?.comment_dropdowns, "comment", row.id, row.uuid)}
                                    </div>
                                    <CommentTags tags={row?.tags} />
                                  </TableCell>
                                  {showRatingColumn ? (
                                    <TableCell className="cl-summary-col" align="left">
                                      {<SummaryRating rating={row?.ratings} />}
                                    </TableCell>
                                  ) : null}
                                  <TableCell align="left" style={{ position: "relative" }}>
                                    <div style={{ width: "50px" }}>
                                      <span className="date-placeholder-comment">{moment(row?.updated_at)?.format("MM/DD/YYYY")}</span>
                                      <span className="time-placeholder">{moment(row?.updated_at)?.format("LT")}</span>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            }) : <TableRow style={{ height: "100%" }}>
                              <TableCell colSpan={999} style={{ padding: 0 }}>
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  alignItems="center"
                                  justifyContent="center"
                                  height="100%"
                                  minHeight="360px" // force some height if table has no rows
                                  textAlign="center"
                                >
                                  <Typography className="cl-empty-text">{hasActiveFilters ? "No results found" : "Comment library is empty"}</Typography>
                                </Box>
                              </TableCell>
                            </TableRow>}
                          </TableBody>
                        </AnimatePresence>
                      </Table>
                    </TableContainer >)}
                  <TablePagination
                    component="div"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    count={totalRowCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={
                      !storeData?.isHTCompany || isTexasNarrativeCL
                        ? (p) => <TablePaginationActions {...p} totalPages={totalPages} />
                        : TablePaginationActionsHT
                    }
                  /></Box >
              </TabPanel >
            ) : null}
            <AddNewCommentNN
              value={value}
              newCommentTabIndex={newCommentTabIndex}
              editComment={editComment}
              setStateData={setStateData} stateData={stateData}
              TabPanel={TabPanel}
              AllCategoryList={AllCategoryList}
              categoryId={categoryId}
              recommendation={recommendation}
              editorContent={editorContent}
              setEditorContent={setEditorContent}
              newOpen={open}
            />
          </Grid >
        </Grid >
      </DialogContent >
      <Divider />
      <DialogActions className="non-narrative-dialog-actions">
        {isNewCommentTab ? <FormGroup>
          <FormControlLabel
            className="cl_checkbox"
            control={
              <Checkbox
                checked={addToLibrary}
                onChange={() => setStateData(prevState => ({ ...prevState, addToLibrary: !addToLibrary }))}
                sx={{
                  height: "24px !important",
                  width: "24px !important",
                  marginRight: "8px",
                  marginLeft: "20px",
                  "& .MuiSvgIcon-root": { height: "24px !important", width: "24px !important" },
                }}
              />
            }
            label="Save to comment library"
          />
        </FormGroup> : null}
        <Grid container item xs={isNewCommentTab ? 9 : 12} display={"flex"} justifyContent={"flex-end"} gap={2}>
          <Button variant="outlined" className="non-narrative-comment-cancel" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            className="non-narrative-comment-save"
            disabled={disableCommentBtn || (isNewCommentTab ? commentTitle === "" && (editorContent?.innerHTML === "" || preprocessHTMLContent(editorContent?.innerHTML) === "") : selected?.length === 0 || isPoolRefreshing)}
            onClick={() => isNewCommentTab ? createNewComment() : handleAddSelectedComment()}
          >
            {isNewCommentTab ? "Add new comment" : `Add ${selected?.length} comments`}
          </Button>
        </Grid>
      </DialogActions>
    </Dialog >
  );
};

export default NonNarrativeAddComment;